import React, { useState } from 'react';
import './AIRelanceGenerator.css';

const AIRelanceGenerator = ({ documentId, documentType, currentStatus, onRelanceGenerated }) => {
  const [loading, setLoading] = useState(false);
  const [generatedRelance, setGeneratedRelance] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [error, setError] = useState(null);

  const generateRelance = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-relance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: documentId,
          document_type: documentType,
          current_status: currentStatus,
          context: {
            last_contact: new Date().toISOString(),
            document_age_days: Math.floor((new Date() - new Date()) / (1000 * 60 * 60 * 24))
          },
          custom_instructions: customInstructions || null
        })
      });

      if (response.ok) {
        const relanceData = await response.json();
        setGeneratedRelance(relanceData);
        if (onRelanceGenerated) {
          onRelanceGenerated(relanceData);
        }
      } else {
        throw new Error('Erreur lors de la génération de la relance');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error generating AI relance:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetGenerator = () => {
    setGeneratedRelance(null);
    setCustomInstructions('');
    setError(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Texte copié dans le presse-papiers');
  };

  return (
    <div className="ai-relance-generator">
      <div className="generator-header">
        <h3>🤖 Générateur IA de Relance</h3>
        <p className="generator-subtitle">
          Génération automatique de relance personnalisée pour {documentType} - Statut: {currentStatus}
        </p>
      </div>

      {!generatedRelance ? (
        <div className="generator-form">
          <div className="form-group">
            <label htmlFor="customInstructions">Instructions personnalisées (optionnel)</label>
            <textarea
              id="customInstructions"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Ex: Ton plus commercial, mentionner la promo en cours, urgence du projet..."
              rows="3"
              className="instructions-textarea"
            />
          </div>

          <div className="generator-actions">
            <button
              onClick={generateRelance}
              disabled={loading}
              className="generate-btn"
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Génération en cours...
                </>
              ) : (
                <>
                  🤖 Générer la relance IA
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon"></span>
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="generated-relance">
          <div className="relance-header">
            <h4>📧 Relance générée par IA</h4>
            <div className="relance-meta">
              <span className="confidence-score">
                🎯 Confiance: {Math.round(generatedRelance.confidence_score * 100)}%
              </span>
              <span className="tone-indicator">
                🎭 Ton: {generatedRelance.suggested_tone}
              </span>
            </div>
          </div>

          <div className="relance-content">
            <div className="subject-section">
              <div className="section-header">
                <label>📌 Objet de l'email</label>
                <button
                  onClick={() => copyToClipboard(generatedRelance.suggested_subject)}
                  className="copy-btn"
                >
                  📋 Copier
                </button>
              </div>
              <div className="subject-text">
                {generatedRelance.suggested_subject}
              </div>
            </div>

            <div className="body-section">
              <div className="section-header">
                <label>📝 Corps du message</label>
                <button
                  onClick={() => copyToClipboard(generatedRelance.suggested_body)}
                  className="copy-btn"
                >
                  📋 Copier
                </button>
              </div>
              <div className="body-text">
                <pre>{generatedRelance.suggested_body}</pre>
              </div>
            </div>

            {generatedRelance.reasoning && (
              <div className="reasoning-section">
                <details>
                  <summary>💡 Justification de l'IA</summary>
                  <div className="reasoning-text">
                    {generatedRelance.reasoning}
                  </div>
                </details>
              </div>
            )}
          </div>

          <div className="relance-actions">
            <button
              onClick={() => copyToClipboard(`Objet: ${generatedRelance.suggested_subject}\n\n${generatedRelance.suggested_body}`)}
              className="copy-all-btn"
            >
              📋 Copier tout
            </button>
            <button
              onClick={resetGenerator}
              className="regenerate-btn"
            >
              🔄 Générer une nouvelle relance
            </button>
          </div>
        </div>
      )}

      <div className="generator-info">
        <small>
          💡 <strong>Astuce:</strong> L'IA analyse le contexte du document et son statut pour 
          générer une relance pertinente. Plus vous donnez d'instructions personnalisées, 
          plus la relance sera adaptée à votre situation.
        </small>
      </div>
    </div>
  );
};

export default AIRelanceGenerator;